import React, { useRef, useCallback, cloneElement, Children } from 'react';

export interface PreventClickOnDragProps {
  children: React.ReactElement;
  onClick: React.MouseEventHandler<HTMLElement>;
  clickTolerance?: number;
}

/**
 * Envuelve un hijo y solo propaga onClick si no hubo arrastre por encima de clickTolerance píxeles.
 */
export function PreventClickOnDrag({ children, onClick, clickTolerance }: PreventClickOnDragProps) {
  const originClickX = useRef<number | null>(null);
  const cancelClick = useRef(false);

  const handleMouseDown = useCallback<React.MouseEventHandler<HTMLElement>>((evt) => {
    originClickX.current = evt.clientX;
  }, []);

  const handleMouseUp = useCallback<React.MouseEventHandler<HTMLElement>>(
    (evt) => {
      if (
        clickTolerance !== undefined &&
        originClickX.current !== null &&
        Math.abs(originClickX.current - evt.clientX) > clickTolerance
      ) {
        cancelClick.current = true;
      }
    },
    [clickTolerance],
  );

  const handleClick = useCallback<React.MouseEventHandler<HTMLElement>>(
    (evt) => {
      if (!cancelClick.current) {
        onClick(evt);
      }
      cancelClick.current = false;
      originClickX.current = null;
    },
    [onClick],
  );

  const childElement = Children.only(children);
  return cloneElement(childElement, {
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onClick: handleClick,
  });
}

PreventClickOnDrag.displayName = 'PreventClickOnDrag';

export default PreventClickOnDrag;
